import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import CompanySetup from './pages/CompanySetup';
import AcceptInvitation from './pages/AcceptInvitation';
import UserManagement from './pages/UserManagement';
import Profile from './pages/Profile';
import Trash from './pages/Trash';
import LoadingScreen from './components/LoadingScreen';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Protected route component
const PrivateRoute: React.FC<{
  children: React.ReactNode;
  adminOnly?: boolean;
  minRole?: 'treasurer' | 'admin';
}> = ({
  children,
  adminOnly,
  minRole,
}) => {
  const { isAuthenticated, isLoading, needsCompany, isAdmin, isTreasurer } = useAuth();

  if (isLoading) {
    return <LoadingScreen text="Подготовка данных..." subtext="Вы будете перенаправлены" />;
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (needsCompany) return <Navigate to="/setup-company" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/dashboard" replace />;
  if (minRole === 'treasurer' && !isAdmin && !isTreasurer) return <Navigate to="/dashboard" replace />;
  if (minRole === 'admin' && !isAdmin) return <Navigate to="/dashboard" replace />;

  return <>{children}</>;
};

// Public route - redirect to dashboard if authenticated
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, needsCompany } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated && needsCompany) return <Navigate to="/setup-company" replace />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  return <>{children}</>;
};

// Company setup route - only for authenticated users without a company
const CompanySetupRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, needsCompany } = useAuth();

  if (isLoading) {
    return <LoadingScreen text="Подготовка данных..." />;
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!needsCompany) return <Navigate to="/dashboard" replace />;

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/setup-company"
        element={
          <CompanySetupRoute>
            <CompanySetup />
          </CompanySetupRoute>
        }
      />
      <Route
        path="/invite/:token"
        element={<AcceptInvitation />}
      />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/users"
        element={
          <PrivateRoute adminOnly>
            <UserManagement />
          </PrivateRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <PrivateRoute>
            <Profile />
          </PrivateRoute>
        }
      />
      <Route
        path="/trash"
        element={
          <PrivateRoute>
            <Trash />
          </PrivateRoute>
        }
      />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
};

export default App;
